// Modules
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useMutation } from '@apollo/client';
import { CONFIRM_PAYMENT } from '@global/requests/checkout';

import { useAppSelector } from '@global/store/hooks';
import {
  cartSelector,
  subscriptionFrequencySelector,
} from '@global/store/slices/cart/selectors';

// Css and assets
import './style.scss';

// Components
import { useNotification } from '@global/hooks/useNotification';
import { NotificationTypes } from '@global/components/Notification';
import { ButtonCTA } from '@global/components/UI Elements/ButtonCTA';
import { OrderItem } from '@global/components/OrderItem';
import { OrderPrice } from '@global/components/OrderPrice';
import { Terms, SubscriptionInfo, LicenseModal } from './components';

// Utils
import {
  OrderItemType,
  OrderPriceType,
  PreviewDeliveryOption,
} from '@global/utils/orderUtils';
import { useModal } from '@global/hooks/useModal';
import { prefixPath } from '@global/localization/services';
import analytic from '@src/global/utils/AdobeAnalytics';
import { getCustomerIP } from '@global/utils/getCustomerIP';
import { redirectGuestToAuth } from '@global/utils/guest';

// Constants and Types
import {
  Pages,
  CART_CHECKOUT_BASE_URL,
  TEXTBOOK_RENTAL_CAT_ID,
  CART_UID_URL_PARAM_KEY,
  TEXTBOOK__ALTO_RENTAL_CAT_ID,
} from '@global/App/constants/constants';
import { DigitalFormat } from '@global/types/types';
import { getSubscriptionInfoDate, Suffixes } from '@global/utils/date';
import { getTokensFromCookies } from '@global/hooks/useAuth/utils';
import { SiteConfig } from '@global/utils/SiteConfig';

interface IProps {
  orderItems?: OrderItemType[];
  orderPrice?: OrderPriceType;
  deliveryOption?: PreviewDeliveryOption;
  isLoading?: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isSummaryLoading?: boolean;
  cartId: string;
  onSuccess: () => void;
  backToTheShippingStep: () => void;
}

export const OrderReview: React.FC<IProps> = ({
  orderItems,
  orderPrice,
  deliveryOption,
  isLoading,
  setIsLoading,
  isSummaryLoading,
  cartId,
  onSuccess,
  backToTheShippingStep,
}) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const cartState = useAppSelector(cartSelector);
  const { setShowNotification } = useNotification();

  const [error, setError] = useState('');
  const [agreement, setAgreement] = useState(false);
  const [professionalUseAgreement, setProfessionalUseAgreement] =
    useState(false);
  const { isAigateway } = SiteConfig.getConfig();

  useEffect(() => {
    error && setShowNotification([NotificationTypes.error, error]);
  }, [error]);

  const [confirmPayment] = useMutation(CONFIRM_PAYMENT);

  const toggleAgreement = () => setAgreement((prev) => !prev);
  const toggleProfessionalUseAgreement = () =>
    setProfessionalUseAgreement((prev) => !prev);

  const { handleOpen: openLicenseModal } = useModal<boolean>({
    hideCloseButton: true,
    customClass: 'license-modal',
    content: ({ handleClose }) => (
      <LicenseModal
        orderItems={orderItems}
        handleClose={handleClose}
        handleAccept={() => confirmPaymentHandler(true)}
      />
    ),
  });

  const confirmPaymentHandler = async (licenseAccepted) => {
    const hasLicensedProducts = orderItems?.some(
      (item) => item.licenseAgreementInfo
    );
    if (hasLicensedProducts && !licenseAccepted) {
      return openLicenseModal(true);
    }

    setIsLoading(true);
    const ipAddress = getCustomerIP();

    const { viaxToken, almToken } = getTokensFromCookies();

    const { data } = await confirmPayment({
      variables: {
        uid: cartId,
        clientIp: ipAddress || '',
        ecomToken: viaxToken,
        almToken,
      },
    });

    if (
      data?.wConfirmPayment?.selectedDeliveryOptionIsNotApplicable ||
      data?.wConfirmPayment?.inconsistentDeliveryData
    ) {
      setShowNotification([
        NotificationTypes.error,
        t('checkout.review.validationErr.deliveryOption'),
      ]);
      backToTheShippingStep();
    } else if (data?.wConfirmPayment?.hasUnavailableProducts) {
      const cartUrl = CART_CHECKOUT_BASE_URL.replace(':locale', i18n.language);
      window.location.href = `${cartUrl}${window.location.search}`;
    } else if (data?.wConfirmPayment?.guestModeNotAllowed) {
      redirectGuestToAuth();
    } else if (data?.wConfirmPayment?.status === 'OK') {
      analytic.dataLayerPlaceOrderClick();
      setIsLoading(false);
      onSuccess();
      history.push({
        pathname: SiteConfig.isNonLocalizedSite()
          ? Pages.ORDER_CONFIRMATION
          : prefixPath(Pages.ORDER_CONFIRMATION, i18n.language),
        search: `?${CART_UID_URL_PARAM_KEY}=${cartId}`,
      });
    } else {
      setIsLoading(false);
      setError(t('checkout.review.validationErr.error'));
    }
    setError('');
  };

  const isCartEmpty = orderItems && !orderItems.length;
  const isDigital = orderItems.some(
    ({ format }) => DigitalFormat.DIGITAL === format
  );
  const rentalCategoryIds = [
    TEXTBOOK__ALTO_RENTAL_CAT_ID,
    TEXTBOOK_RENTAL_CAT_ID,
  ];

  const withRental = orderItems.some(({ categoryId }) =>
    rentalCategoryIds.includes(categoryId)
  );

  const isFreeTrial = cartState.isFreeTrial;

  const disabledByAgreement =
    ((withRental || cartState.isSubscription) && !agreement) ||
    (isAigateway && cartState.isSubscription && !professionalUseAgreement);
  const disabledCta = cartState.isLoading || isLoading || disabledByAgreement;

  const subscriptionFrequency = useAppSelector(subscriptionFrequencySelector);

  const suffixes: Suffixes = {
    one: t('common.ordinalSuffix.st'),
    two: t('common.ordinalSuffix.nd'),
    few: t('common.ordinalSuffix.rd'),
    other: t('common.ordinalSuffix.th'),
  };

  const subscriptionInfoDate = getSubscriptionInfoDate(
    isFreeTrial,
    cartState?.discounts[0]?.freeTrialDuration?.amount,
    suffixes
  );

  const subscriptionInfo: SubscriptionInfo = {
    planName: orderItems[0]?.maName,
    billingPeriod: subscriptionFrequency,
    currency_symbol: orderItems[0]?.price.code,
    price: orderItems[0]?.price.value.toFixed(2),
    nextPaymentDate:
      t(
        `subscription.${subscriptionFrequency}.nextPaymentDate`,
        subscriptionInfoDate
      ) || '',
    frequency:
      t(`subscription.${subscriptionFrequency}.frequencyWithArticles`) || '',
    day: subscriptionInfoDate.day,
  };

  return (
    <div
      className={
        !isCartEmpty ? 'order-review' : 'order-review order-review-empty'
      }
    >
      <div className="order-review__wrapper">
        <div className="order-review__content">
          {isSummaryLoading ? (
            <div className="spinner" id="spinner"></div>
          ) : (
            <>
              {/* LIST OF ITEMS BEING PURCHASED */}
              {!isCartEmpty &&
                orderItems?.map((item, index) => {
                  const discount = cartState.discounts.find(
                    (d) => d.itemNumber === item.biiId
                  );
                  return (
                    <OrderItem
                      key={item.maName + index}
                      orderItem={item}
                      discount={discount}
                      isSubscription={cartState.isSubscription}
                    />
                  );
                })}

              <OrderPrice
                deliveryOption={deliveryOption}
                orderPrice={orderPrice}
                isFreeTrial={isFreeTrial}
                isSubscription={cartState.isSubscription}
              />

              {isDigital && !cartState.isSubscription ? (
                <p className="order-review__note">
                  <strong className="bold">
                    {t('checkout.review.digitalNote.title')}
                  </strong>{' '}
                  {t('checkout.review.digitalNote.description')}
                </p>
              ) : null}

              <Terms
                checked={agreement}
                toggle={toggleAgreement}
                professionalUseChecked={professionalUseAgreement}
                toggleProfessionalUse={toggleProfessionalUseAgreement}
                isAigateway={isAigateway}
                withRental={withRental}
                isSubscription={cartState.isSubscription}
                subscriptionInfo={subscriptionInfo}
              />

              <ButtonCTA
                variant="primary"
                className="order-review__submit-btn"
                disabled={disabledCta}
                onClick={() => confirmPaymentHandler(false)}
              >
                {isLoading ? (
                  <div className="spinner"></div>
                ) : (
                  t('checkout.review.placeorder')
                )}
              </ButtonCTA>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderReview;
